// ---------------------------------------------------------
// Datei: phaseIcons.tsx
// Zweck: Zentrale Icon-Map und Kurz-Labels für Projektphasen
// Verwendet von:
// - PhasePicker (Phasen-Übersicht)
// - PhaseFooterNav (Direktnavigation aus einer Phase, Issue #16)
// ---------------------------------------------------------

import React from 'react';

export type PhaseKey =
  | 'projektdokumentation'
  | 'durchfuehrung'
  | 'inbetriebnahme'
  | 'abschlussbericht'
  | 'uploads'
  | 'stoerungen'
  | 'vorab'
  | 'abschluss'
  | 'unknown';

interface PhaseLike {
  _id?: string;
  name?: string;
}

const ID_SUFFIX_KEYS: PhaseKey[] = [
  'projektdokumentation',
  'durchfuehrung',
  'inbetriebnahme',
  'abschlussbericht',
  'uploads',
  'stoerungen',
  'vorab',
];

// Phase-Key aus _id-Suffix oder Name ableiten.
// Suffix bevorzugt, weil stabil (z.B. "12345-projektdokumentation").
export function getPhaseKey(phase: PhaseLike): PhaseKey {
  const idSuffix = phase._id?.split('-').pop()?.toLowerCase() || '';
  if ((ID_SUFFIX_KEYS as string[]).includes(idSuffix)) {
    return idSuffix as PhaseKey;
  }

  const name = (phase.name || '').toLowerCase();
  if (name.includes('projektdokumentation')) return 'projektdokumentation';
  if (name.includes('abschlussbericht')) return 'abschlussbericht';
  if (name.includes('inbetriebnahme')) return 'inbetriebnahme';
  if (name.includes('vorab')) return 'vorab';
  if (name.includes('allgemeine') || name.includes('uploads')) return 'uploads';
  if (name.includes('störung') || name.includes('stoerung')) return 'stoerungen';
  if (name.includes('durchfuehrung') || name.includes('baustellendoku')) return 'durchfuehrung';
  if (name.includes('abschluss')) return 'abschluss';
  return 'unknown';
}

// Labels für die Footer-Navigation – volle Namen, zweizeilig erlaubt.
// ­ = Soft Hyphen: zeigt nur dann einen Bindestrich, wenn der Browser
// an dieser Stelle umbricht. So bleibt z.B. "Projektdoku" einzeilig auf
// breiten Screens, bricht auf Mobile aber sauber als "Projekt-/doku" um.
export const PHASE_SHORT_LABELS: Record<PhaseKey, string> = {
  projektdokumentation: 'Projekt­doku',
  durchfuehrung: 'Baustellen­doku',
  inbetriebnahme: 'Inbetrieb­nahme',
  abschlussbericht: 'Abschluss­bericht',
  uploads: 'Uploads',
  // Singular vermeidet, dass "en" von "Störungen" als Einzelsilbe alleine
  // umbricht. ZWSP nach dem Slash sorgt für sauberen Break: "Nacharbeit/" / "Störung".
  stoerungen: 'Nacharbeit/​Störung',
  vorab: 'Vorab',
  abschluss: 'Abschluss',
  unknown: 'Phase',
};

interface PhaseIconProps {
  phase: PhaseLike;
  className?: string;
}

// Rendert das passende Heroicon zur Phase.
// Default-Styling entspricht dem bisherigen PhasePicker (w-8 h-8 text-primary-600).
const PhaseIcon: React.FC<PhaseIconProps> = ({ phase, className = 'w-8 h-8 text-primary-600' }) => {
  const key = getPhaseKey(phase);

  switch (key) {
    case 'projektdokumentation':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5h6M9 9h6m-7 8h8a2 2 0 002-2V7a2 2 0 00-2-2H8a2 2 0 00-2 2v8a2 2 0 002 2zm0 0v2m8-2v2M8 21h8" />
        </svg>
      );
    case 'vorab':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      );
    case 'abschlussbericht':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6M8 4h8a2 2 0 012 2v12a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2z" />
        </svg>
      );
    case 'abschluss':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'inbetriebnahme':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    case 'uploads':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'stoerungen':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'durchfuehrung':
      // Bislang fiel "Baustellendoku/Fotos" auf das generische Fallback-Icon zurück.
      // Eigenes Bau-Icon (Hammer/Werkzeug-Stil) wäre hier sauberer; bis dahin: generisches Tool-Icon.
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      );
    case 'unknown':
    default:
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      );
  }
};

export default PhaseIcon;
